import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "../../../CartContext";
import { useCategory } from "../../../contexts/CategoryContext";
import { ChevronLeft, ShieldCheck, Award, HeartHandshake, Info } from "lucide-react";

import ProductLoader from "./ProductLoader";
import { userStore } from "../../../store/userStore";
import toast, { Toaster } from "react-hot-toast";

const alertLoginHandler = () => toast.error("User needs to Login First!");

const ProductDetailComplete = () => {
  const { id: productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { categoryIcon } = useCategory();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState("");
  const [shopDetails, setShopDetails] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const userid = userStore((state) => state._id);

  // Memoized image error handler to prevent unnecessary re-renders
  const handleImageError = useCallback((e) => {
    e.target.style.display = 'none';
    const fallback = e.target.nextSibling;
    if (fallback) {
      fallback.style.display = 'flex';
    }
  }, []);

  useEffect(() => {
    console.log('ProductDetail useEffect - productId:', productId);
    
    // Only run if productId exists and is valid
        // Only run if productId exists and is valid (either a 24-char MongoID or a dummy string)
    if (!productId || (productId.length !== 24 && !productId.startsWith('dummy'))) {
      console.log('Invalid productId:', productId);
      setLoading(false);
      return;
    }

    let isMounted = true; // Flag to prevent state updates if component unmounts
    let requestAbortController = new AbortController(); // For request cancellation

    const dummyProductsList = [
      {
        _id: "dummy1",
        name: "Classic Floral Gold Necklace",
        category: "Necklace",
        description: "A beautiful floral design 22K gold necklace.",
        carat: "22K",
        netWeight: 35.5,
        grossWeight: 36.0,
        price: 245000,
        coverImage: "https://images.unsplash.com/photo-1599643478524-fb66f70d00f8?auto=format&fit=crop&q=80&w=600",
        images: ["https://images.unsplash.com/photo-1599643478524-fb66f70d00f8?auto=format&fit=crop&q=80&w=600"]
      },
      {
        _id: "dummy2",
        name: "Traditional Temple Haar",
        category: "Necklace",
        description: "Handcrafted temple jewelry for weddings.",
        carat: "22K",
        netWeight: 45.0,
        grossWeight: 46.5,
        price: 325000,
        coverImage: "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&q=80&w=600",
        images: ["https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&q=80&w=600"]
      },
      {
        _id: "dummy3",
        name: "Elegant Wedding Rings",
        category: "Ring",
        description: "18K Gold diamond studded couple rings.",
        carat: "18K",
        netWeight: 8.5,
        grossWeight: 8.5,
        price: 85000,
        coverImage: "https://images.unsplash.com/photo-1605100804763-247f67b2548e?auto=format&fit=crop&q=80&w=600",
        images: ["https://images.unsplash.com/photo-1605100804763-247f67b2548e?auto=format&fit=crop&q=80&w=600"]
      },
      {
        _id: "dummy4",
        name: "Gold Jhumka Earrings",
        category: "Earrings",
        description: "Classic 22K gold jhumki earrings.",
        carat: "22K",
        netWeight: 12.0,
        grossWeight: 12.5,
        price: 62000,
        coverImage: "https://images.unsplash.com/photo-1535632787350-4e68ef0ac584?auto=format&fit=crop&q=80&w=600",
        images: ["https://images.unsplash.com/photo-1535632787350-4e68ef0ac584?auto=format&fit=crop&q=80&w=600"]
      },
      {
        _id: "dummy5",
        name: "Bridal Gold Set",
        category: "Bridal Set",
        description: "Complete 24K gold bridal set.",
        carat: "24K",
        netWeight: 120.0,
        grossWeight: 125.0,
        price: 985000,
        coverImage: "https://images.unsplash.com/photo-1543294001-f7cd5d7fb516?auto=format&fit=crop&q=80&w=600",
        images: ["https://images.unsplash.com/photo-1543294001-f7cd5d7fb516?auto=format&fit=crop&q=80&w=600"]
      },
      {
        _id: "dummy6",
        name: "Gold Bangles Set",
        category: "Bangles",
        description: "Set of 4 elegant 22K gold bangles.",
        carat: "22K",
        netWeight: 65.0,
        grossWeight: 66.0,
        price: 512000,
        coverImage: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?auto=format&fit=crop&q=80&w=600",
        images: ["https://images.unsplash.com/photo-1611591437281-460bfbe1220a?auto=format&fit=crop&q=80&w=600"]
      }
    ];

    const fetchProduct = async () => {
      setLoading(true);
      
      // Fallback explicitly to dummy product if it's a dummy ID
      const dummyProd = dummyProductsList.find(p => p._id === productId);
      
      try {
        if (dummyProd) {
          throw new Error("Local dummy id shortcut triggered");
        }

        const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').trim();
        const endpoints = [
          `${baseUrl}/gold/${productId}`,
          `${baseUrl}/Everyday/${productId}`,
          `${baseUrl}/feature/${productId}`,
          `${baseUrl}/best/${productId}`
        ];
        
        let productData = null;
        let foundEndpoint = null;
        
        for (const url of endpoints) {
          try {
            console.log('Trying endpoint:', url);
            const response = await fetch(url, { signal: requestAbortController.signal });
            if (response.ok) {
              const data = await response.json();
              console.log('Product found at:', url, data);
              productData = data;
              foundEndpoint = url;
              break;
            }
          } catch (err) {
            console.log('Endpoint failed:', url, err.message);
            continue;
          }
        }
        
        if (!productData) {
          throw new Error("Product not found in any collection");
        }
        
        if (isMounted) {
          setProduct(productData);
          setSelectedImage(productData.coverImage || productData.images?.[0] || productData.image || "");
        }
      } catch (error) {
        console.error("Error fetching product details, falling back to dummy data:", error);
        if (isMounted) {
          // If the network request failed, use the dummy product we already found, or dummy1 as absolutely last resort
          const fallbackData = dummyProd || dummyProductsList[0];
          setProduct(fallbackData);
          setSelectedImage(fallbackData.coverImage || fallbackData.images?.[0] || "");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const fetchShopDetails = async () => {
      try {
        const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').trim();
        const url = `${baseUrl}/shopdetails`;
        console.log('Fetching shop details from:', url);
        
        const response = await fetch(url, { signal: requestAbortController.signal });
        if (!response.ok) throw new Error("Failed to fetch shop details");
        const data = await response.json();
        console.log('Shop details response:', data);
        
        if (isMounted) {
          setShopDetails(data[0]);
        }
      } catch (error) {
        console.error("Error fetching shop details:", error);
        if (isMounted) {
          setShopDetails(null);
        }
      }
    };

    const fetchRelatedProducts = async () => {
      setLoadingRelated(true);
      try {
        const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').trim();
        const url = `${baseUrl}/related/`;
        console.log('Fetching related products from:', url);
        
        const response = await fetch(url, { signal: requestAbortController.signal });
        if (!response.ok) throw new Error("Failed to fetch related products");
        const data = await response.json();
        console.log('Related products response:', data);
        
        if (isMounted) {
          // Fallback to dummy data if API returned empty array
          setRelatedProducts(data?.length ? data : dummyProductsList);
        }
      } catch (error) {
        console.error("Error fetching related products, falling back to dummy data:", error);
        if (isMounted) {
          setRelatedProducts(dummyProductsList);
        }
      } finally {
        if (isMounted) {
          setLoadingRelated(false);
        }
      }
    };

    fetchProduct();
    fetchShopDetails();
    fetchRelatedProducts();

    return () => {
      isMounted = false;
      requestAbortController.abort(); // Cancel any ongoing requests
    };
  }, [productId]);

  const handleAddToCart = () => {
    console.log("Add to cart clicked - userid:", userid);
    console.log("Add to cart clicked - productId:", productId);
    
    if (!userid) {
      console.log("No user ID found, showing login alert");
      return alertLoginHandler();
    }
    
    const productToAdd = {
      userId: userid,
      productId: productId,
      quantity: 1,
    };
    
    console.log("Calling addToCart with:", productToAdd);
    addToCart(productToAdd);
    navigate("/cart");
  };

  const handleRelatedProductClick = (relatedProduct) => {
    // Navigate to related product or handle click
    console.log("Related product clicked:", relatedProduct);
    // You can navigate to a product detail page or show more info
    // navigate(`/product/${relatedProduct._id}`);
  };

  if (loading) return <ProductLoader />;

  if (!product)
    return (
      <div className="text-center py-12">
        <p className="text-xl text-red-500">Product not found</p>
        <button
          onClick={() => navigate("/shop")}
          className="mt-4 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
        >
          Return to Products
        </button>
      </div>
    );

  const dynamicDescriptionData = shopDetails
    ? [
        { name: "Model", value: shopDetails.modelNumber },
        { name: "Style", value: shopDetails.style },
        { name: "Certificate", value: shopDetails.certificate },
        { name: "Size", value: shopDetails.goldPurity },
        { name: "Total Weight", value: shopDetails.totalWeight },
        {
          name: "Set Includes",
          value: shopDetails.setIncludes?.join(", "),
        },
        {
          name: "Occasion",
          value: shopDetails.occasion?.join(", "),
        },
        {
          name: "Design Theme",
          value: shopDetails.designTheme?.join(", "),
        },
        { name: "Finish", value: shopDetails.finish },
      ]
    : [];

  const productCarat = product.carat || '22K';
  const dummyGoldRates = {
    "18K": 5640,
    "22K": 6850,
    "24K": 7450
  };
  const goldRate = dummyGoldRates[productCarat] || 6850;
  const netWeight = parseFloat(product.netWeight || 29.8);
  
  // Use API calculation if available, otherwise fallback to explicit mock calculation
  const goldValue = product.priceDetails?.goldValue || (netWeight * goldRate);
  const makingCharges = product.priceDetails?.makingCharges || (goldValue * 0.12);
  const gst = product.priceDetails?.gst || ((goldValue + makingCharges) * 0.03);
  const totalPrice = product.priceDetails?.totalPrice || (goldValue + makingCharges + gst);

  return (
    <div className="bg-[#fdfcf8] min-h-screen font-sans border-t border-[#D4AF37]/20 pb-20 pt-2">
      {/* Breadcrumb / Back Navigation */}
      <div className="max-w-[1300px] mx-auto px-4 sm:px-8 py-6">
        <div 
          onClick={() => navigate("/shop")}
          className="flex items-center text-gray-500 cursor-pointer hover:text-black transition-colors w-fit"
        >
          <ChevronLeft className="w-5 h-5 mr-1" strokeWidth={1.5} />
          <span className="text-[13px] font-medium tracking-wide">Back to Collections</span>
        </div>
      </div>

      {/* Main Content */}
      <div className=" max-w-[1300px] mx-auto px-4 sm:px-8 flex flex-col lg:flex-row gap-12 lg:gap-16">
        {/* Left: Images */}
        <div className="w-full lg:w-1/2 flex flex-col gap-4">
          {/* Main Image */}
          <div className="relative w-full aspect-square md:aspect-[4/5] bg-white border border-[#eae0d5]/50 overflow-hidden group">
             {selectedImage ? (
               <img
                 src={selectedImage}
                 alt={product.name || "Product"}
                 className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                 onError={handleImageError}
               />
             ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">No Image Available</div>
             )}
             {/* Tag */}
             {product.tag || product.category ? (
               <div className="absolute top-5 right-5 bg-[#C19A5B] text-white text-[11px] px-3.5 py-1.5 font-medium tracking-widest uppercase shadow-sm">
                 {product.tag || "Meena Work"}
               </div>
             ) : null}
          </div>
          {/* Thumbnails */}
          <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {product.images?.filter(img => img && img.trim() !== '').slice(0, 3).map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`Thumbnail ${index + 1}`}
                className={`w-24 h-24 md:w-32 md:h-32 object-cover cursor-pointer transition-all border ${
                  selectedImage === img ? "border-[#C19A5B] opacity-100 ring-1 ring-[#C19A5B]" : "border-gray-200 opacity-70 hover:opacity-100"
                }`}
                onClick={() => setSelectedImage(img)}
                onError={handleImageError}
              />
            ))}
          </div>
        </div>

        {/* Right: Info */}
        <div className="w-full lg:w-1/2 flex flex-col pt-2 md:pt-4">
          <p className="text-gray-400 text-[11px] uppercase tracking-widest mb-1.5 font-semibold">
             {product.category || 'Necklace'}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-serif text-[#C19A5B] mb-5 leading-tight">
             {product.name}
          </h1>
          
          <div className="flex flex-wrap gap-2 mb-6">
             <span className="bg-[#fbf9f4] text-[#b88c3a] text-[11px] px-4 py-1.5 font-medium border border-[#eae0d5]">
               BIS Hallmarked
             </span>
             <span className="bg-[#fbf9f4] text-[#b88c3a] text-[11px] px-4 py-1.5 font-medium border border-[#eae0d5]">
               {product.carat || '22K (91.6% pure)'}
             </span>
          </div>

          <div className="mb-6">
             <div className="flex items-center gap-3">
               <span className="text-3xl font-serif text-[#C19A5B] font-semibold tracking-wide">
                 ₹ {totalPrice.toLocaleString('en-IN', {maximumFractionDigits: 0})}
               </span>
             </div>
             <p className="text-gray-400 text-[11px] italic mt-2">*Price may vary with current gold rates</p>
          </div>

          {/* Weight Specifications */}
          <div className="bg-[#fbf9f4] p-5 md:p-6 mb-6">
            <h3 className="text-[#C19A5B] font-medium mb-4 text-[13px] tracking-wide">Weight Specifications</h3>
            <div className="space-y-3.5 text-[13px]">
               <div className="flex justify-between items-center text-gray-700 border-b border-[#eae0d5]/50 pb-3">
                 <span className="font-light">Gross Weight:</span>
                 <span className="font-semibold text-[#b88c3a]">{product.grossWeight || '32.40'} grams</span>
               </div>
               <div className="flex justify-between items-center text-gray-700 border-b border-[#eae0d5]/50 pb-3">
                 <span className="font-light flex items-center gap-1.5">Net Gold Weight: <Info className="w-3.5 h-3.5 text-gray-400" /></span>
                 <span className="font-semibold text-[#b88c3a]">{product.netWeight || '29.80'} grams</span>
               </div>
               <div className="flex justify-between items-center text-gray-700">
                 <span className="font-light">Metal Purity:</span>
                 <span className="font-semibold text-[#b88c3a]">{product.carat || '22K (91.6% pure)'}</span>
               </div>
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="border border-[#eae0d5] p-5 md:p-6 mb-8 bg-white">
            <h3 className="text-[#b88c3a] font-medium mb-4 text-[13px] tracking-wide">Price Breakdown</h3>
            <div className="space-y-3.5 text-[13px] border-b border-gray-100 pb-5 mb-5">
               <div className="flex justify-between items-center text-gray-600">
                 <span className="font-light text-[13px]">Gold Value ({netWeight}g × ₹{goldRate.toLocaleString('en-IN')}/g):</span>
                 <span className="font-medium text-[13px] text-gray-800">₹ {goldValue.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
               </div>
               <div className="flex justify-between items-center text-gray-600">
                 <span className="font-light text-[13px]">Making Charges (12%):</span>
                 <span className="font-medium text-[13px] text-gray-800">₹ {makingCharges.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
               </div>
               <div className="flex justify-between items-center text-gray-600">
                 <span className="font-light text-[13px]">GST (3%):</span>
                 <span className="font-medium text-[13px] text-gray-800">₹ {gst.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
               </div>
            </div>
            <div className="flex justify-between items-center">
               <span className="text-[#b88c3a] font-semibold text-[13px]">Approximate Total:</span>
               <span className="text-[#b88c3a] font-serif font-bold text-2xl">₹ {totalPrice.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
            </div>
            <p className="text-gray-400 text-[10px] italic mt-3 leading-tight">*This is an approximate calculation based on current rates. Final price will be calculated at the time of purchase.</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 mb-10">
            <button 
              onClick={handleAddToCart}
              className="w-full bg-[#b88c3a] hover:bg-[#a37a30] text-white font-medium py-[15px] text-[13px] tracking-[0.1em] transition-colors shadow-sm"
              disabled={product.isAvailable === false}
            >
              RESERVE THIS PIECE
            </button>
            <button className="w-full bg-transparent border border-[#b88c3a] hover:bg-[#fbf9f4] text-[#b88c3a] font-medium py-[15px] text-[13px] tracking-[0.1em] transition-colors">
              SCHEDULE SHOWROOM VISIT
            </button>
          </div>

          {/* Product Features */}
          <div className="pt-2">
            <h3 className="text-[#b88c3a] text-[13px] font-medium mb-4">Product Features</h3>
            <ul className="space-y-3.5 text-[13px] text-gray-600 font-light">
               <li className="flex items-center gap-3">
                 <ShieldCheck className="w-[18px] h-[18px] text-[#b88c3a]" strokeWidth={1.5} />
                 <span>100% BIS Hallmarked for guaranteed purity</span>
               </li>
               <li className="flex items-center gap-3">
                 <Award className="w-[18px] h-[18px] text-[#b88c3a]" strokeWidth={1.5} />
                 <span>Handcrafted by skilled artisans</span>
               </li>
               <li className="flex items-center gap-3">
                 <HeartHandshake className="w-[18px] h-[18px] text-[#b88c3a]" strokeWidth={1.5} />
                 <span>Lifetime exchange and buyback guarantee</span>
               </li>
            </ul>
          </div>
        </div>
      </div>

      {/* About this Piece */}
      <div className="w-full bg-[#f4ead5] py-16 px-6 sm:px-12 lg:px-24 mt-16 mb-16">
         <div className="max-w-[1300px] mx-auto">
           <h2 className="text-xl md:text-2xl font-serif text-[#C19A5B] mb-8 tracking-wide">About This Piece</h2>
           <div className="max-w-4xl space-y-5 text-[14px] text-[#555] font-light leading-relaxed">
              <p>This exquisite {product.name?.toLowerCase() || 'jewelry'} represents the perfect blend of traditional craftsmanship and contemporary design. Each piece is meticulously handcrafted by our skilled artisans, ensuring exceptional quality and attention to detail.</p>
              <p>Made from {product.carat || '22K (91.6% pure)'} gold and weighing {product.grossWeight || '32.40'} grams, this piece is perfect for weddings and special occasions. {product.description || 'The intricate work adds a touch of traditional artistry, making it a truly unique piece.'}</p>
           </div>
         </div>
      </div>

      {/* Care Instructions */}
      <div className="w-full mb-20 px-4 sm:px-8">
         <div className="max-w-[1300px] mx-auto">
           <h2 className="text-xl md:text-2xl font-serif text-[#C19A5B] mb-8 tracking-wide">Care Instructions</h2>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-[#f4ead5] p-7 transition-colors hover:bg-[#e8dec9]">
                 <h3 className="text-[#b88c3a] text-[13px] font-medium tracking-wider mb-4">Cleaning</h3>
                 <p className="text-[12px] text-[#555] font-light leading-relaxed">Clean with a soft cloth and mild soap solution. Avoid harsh chemicals and ultrasonic cleaners for pieces with meena work.</p>
              </div>
              <div className="bg-[#f4ead5] p-7 transition-colors hover:bg-[#e8dec9]">
                 <h3 className="text-[#b88c3a] text-[13px] font-medium tracking-wider mb-4">Storage</h3>
                 <p className="text-[12px] text-[#555] font-light leading-relaxed">Store in a soft cloth pouch separately from other jewelry to prevent scratches. Keep away from moisture and direct sunlight.</p>
              </div>
              <div className="bg-[#f4ead5] p-7 transition-colors hover:bg-[#e8dec9]">
                 <h3 className="text-[#b88c3a] text-[13px] font-medium tracking-wider mb-4">Wearing</h3>
                 <p className="text-[12px] text-[#555] font-light leading-relaxed">Remove jewelry before swimming, bathing, or engaging in physical activities. Apply perfumes and cosmetics before wearing.</p>
              </div>
              <div className="bg-[#f4ead5] p-7 transition-colors hover:bg-[#e8dec9]">
                 <h3 className="text-[#b88c3a] text-[13px] font-medium tracking-wider mb-4">Maintenance</h3>
                 <p className="text-[12px] text-[#555] font-light leading-relaxed">Bring to our showroom for professional cleaning and inspection annually. We offer complimentary cleaning services.</p>
              </div>
           </div>
         </div>
      </div>
      
      <Toaster />
    </div>
  );
};

export default ProductDetailComplete;
